import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, MapPin, CreditCard, Package } from "lucide-react";
import { motion } from "framer-motion";
import Logo from "./Logo";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#09090B]/70 border-b border-white/10"
      data-testid="main-navbar"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" aria-label="AB fëlise shop — home">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <Link
            to="/shop"
            data-testid="nav-shop-link"
            className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors duration-300"
          >
            Collezione
          </Link>
          <a
            href="/#filosofia"
            data-testid="nav-philosophy-link"
            className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors duration-300"
          >
            Filosofia
          </a>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-account-button"
                  className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors duration-300"
                  aria-label="Account"
                >
                  <User size={17} strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#121214] border-white/10 text-foreground">
                <DropdownMenuItem className="text-xs uppercase tracking-[0.2em] text-muted-foreground focus:bg-white/5 focus:text-foreground">
                  {user.name || user.email}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-addresses-link"
                  onClick={() => navigate("/account/indirizzi")}
                  className="focus:bg-white/5 focus:text-foreground cursor-pointer"
                >
                  <MapPin size={14} className="mr-2" /> I miei indirizzi
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-orders-link"
                  onClick={() => navigate("/account/ordini")}
                  className="focus:bg-white/5 focus:text-foreground cursor-pointer"
                >
                  <Package size={14} className="mr-2" /> I miei ordini
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-cards-link"
                  onClick={() => navigate("/account/metodi-di-pagamento")}
                  className="focus:bg-white/5 focus:text-foreground cursor-pointer"
                >
                  <CreditCard size={14} className="mr-2" /> Metodi di pagamento
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-logout-button"
                  onClick={logout}
                  className="focus:bg-white/5 focus:text-foreground cursor-pointer"
                >
                  <LogOut size={14} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              data-testid="nav-account-button"
              onClick={() => navigate("/auth")}
              className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors duration-300"
              aria-label="Accedi"
            >
              <User size={17} strokeWidth={1.5} />
            </button>
          )}

          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative h-11 px-5 rounded-full bg-primary text-primary-foreground flex items-center gap-2 hover:bg-[#c8ad8f] transition-colors duration-300"
            aria-label="Apri carrello"
          >
            <ShoppingBag size={16} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.2em] font-medium hidden sm:inline">
              Carrello
            </span>
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-foreground text-background text-[10px] font-semibold flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </motion.header>
  );
}
